import './styles/PrioritizationAndApproval.scss';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Button, FormLabel, Grid, Paper, Stack, Switch, Typography } from '@mui/material';

import { FieldArray, useFormikContext } from 'formik';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { SubjectType } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { ParticipantConfiguration } from 'app/containers/AWPM-FWPM/Modules/ModuleModal/components/ParticipantConfiguration/ParticipantConfiguration';
import { translations } from 'locales/i18n';

import '../styles/Prioritization.scss';

interface Props {
  subjectType: SubjectType;
}
export default function PrioritizationAndApproval({ subjectType }: Props) {
  const { t } = useTranslation();
  const ref = useRef<null | HTMLDivElement>(null);

  const { values, setFieldValue } = useFormikContext<{
    maxParticipants: number;
    participantsConfigurations: any[];
    distributeEvenly: boolean;
    fillUpByKey: boolean;
    subjectType: SubjectType;
    isDefault: boolean;
  }>();

  const availableSeats = () => {
    const result = seatsCounter();
    return result >= 0
      ? result + ' ' + t(translations.pages.modules.create.prioritizationAndApproval.openSeats())
      : `${Math.abs(result)} ${t(
          translations.pages.modules.create.prioritizationAndApproval.reservedMoreSeats()
        )}`;
  };

  const seatsCounter = () => {
    let unusedSeats =
      values.participantsConfigurations.length === 0
        ? 0
        : values.participantsConfigurations.reduce((prevValue, value) => ({
            seats: Number(prevValue.seats) + Number(value.seats)
          })).seats;
    if (isNaN(unusedSeats)) {
      return values.maxParticipants;
    }
    return values.maxParticipants - unusedSeats;
  };

  return (
    <Grid container>
      {subjectType === SubjectType.Fwpm && (
        <Grid item xs={12}>
          {t(translations.pages.modules.create.prioritizationAndApproval.title())}
        </Grid>
      )}
      <FieldArray name="participantsConfigurations">
        {({ remove, push }) => (
          <>
            <Grid className="prioritization-approval__list" container spacing={2} ref={ref}>
              {values.participantsConfigurations.map((parConf, index) => {
                return (
                  <Grid
                    item
                    xs={12}
                    key={
                      parConf.courseOfStudyIds[0]
                        ? 'courseOfStudy' + parConf.courseOfStudyIds.join('-') + 'index' + index
                        : 'index' + index
                    }
                  >
                    <ParticipantConfiguration
                      key={
                        parConf.courseOfStudyIds[0]
                          ? 'courseOfStudyParConf' +
                            parConf.courseOfStudyIds.join('-') +
                            'indexParConf' +
                            index
                          : 'indexParConf' + index
                      }
                      participantConfig={parConf}
                      index={index}
                      remove={remove}
                    />
                  </Grid>
                );
              })}
            </Grid>
            <Grid
              className="prioritization-approval__controls"
              container
              justifyContent="space-between"
              spacing={1}
            >
              {!(values.participantsConfigurations.length > 0) && (
                <Grid item xs={12}>
                  {t(
                    translations.pages.modules.create.prioritizationAndApproval.altLeastOneConfigurationRequired()
                  )}
                </Grid>
              )}
              <Grid item>
                <Button
                  onClick={() => {
                    push({
                      seats: null,
                      custom: false,
                      courseOfStudyIds: [],
                      excludedCourseOfStudyIds: [],
                      admissionCriteria: [],
                      prioritization: [],
                      hasAutomaticSeatDistribution: false,
                      isGlobal: false,
                      excludeCourses: false
                    });
                    setTimeout(() => {
                      //! Get new created participant configuration
                      (ref.current?.lastChild as HTMLElement).scrollIntoView({
                        behavior: 'smooth'
                      });
                    }, 100);
                  }}
                  key="addNew"
                  variant="contained"
                  color="primary"
                  style={{ color: 'white' }}
                  type="button"
                >
                  {t(translations.pages.modules.create.prioritizationAndApproval.addNew())}
                </Button>
              </Grid>
              <Grid container item xs justifyContent="flex-end">
                <div
                  className={seatsCounter() >= 0 ? 'prioritization__true' : 'prioritization__false'}
                >
                  {(values.maxParticipants || 0) + ' '}
                  {t(translations.pages.modules.create.prioritizationAndApproval.participants())}
                  {` | ${availableSeats()} `}
                </div>
              </Grid>
            </Grid>
            <Grid className="prioritization-approval__distribute-seats" container component={Paper}>
              {!values.fillUpByKey && !values.distributeEvenly && (
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <WarningAmberIcon className="warning-icon__icon" />
                  </Grid>
                  <Grid item>
                    <Typography>
                      {t(
                        translations.pages.modules.create.prioritizationAndApproval.configWarning()
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              <Grid container>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography>{t(translations.common.off())}</Typography>
                  <Switch
                    checked={values.fillUpByKey}
                    value={values.fillUpByKey}
                    onChange={(_e, checked) => setFieldValue('fillUpByKey', checked)}
                  />
                  <Typography>{t(translations.common.on())}</Typography>
                  <FormLabel sx={{ marginLeft: 1 }}>
                    {t(translations.pages.modules.create.prioritizationAndApproval.fillUpByKey())}
                  </FormLabel>
                </Stack>
              </Grid>
              <Grid container>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography>{t(translations.common.off())}</Typography>
                  <Switch
                    checked={values.distributeEvenly}
                    value={values.distributeEvenly}
                    onChange={(_e, checked) => setFieldValue('distributeEvenly', checked)}
                  />
                  <Typography>{t(translations.common.on())}</Typography>
                  <FormLabel sx={{ marginLeft: 1 }}>
                    {t(
                      translations.pages.modules.create.prioritizationAndApproval.distributeEvenly()
                    )}
                  </FormLabel>
                </Stack>
              </Grid>
            </Grid>
          </>
        )}
      </FieldArray>
    </Grid>
  );
}
